/**
 * Processing Status Helpers
 * =========================
 *
 * Two exported utilities:
 *
 * 1. `getStaleProcessingResets`  (pure, no Firebase deps)
 *    Inspects every `*ProcessingStatus` / `*ProcessingStartedAt` pair in the
 *    `6_manageReports` Firestore sub-document and returns a map of fields to
 *    reset to 'idle' when stuck at 'processing' beyond the allowed maximum
 *    (default 10 min).  Consumed by the DocumentsDashboard onSnapshot listener
 *    for self-healing.
 *
 * 2. `waitForProcessingComplete`  (uses Firestore onSnapshot)
 *    Returns a promise that resolves once a specific `*ProcessingStatus` field
 *    transitions away from 'processing' (i.e. to 'complete', 'error', 'idle',
 *    or any non-processing value).  Used by the auto-helpers (autoPatientReport,
 *    autoCoverPage) to wait for an in-flight generation to finish before
 *    re-checking staleness — this is how we guarantee correctness when multiple
 *    review exits fire concurrently.
 */

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Maps each processing-status field to the companion timestamp field
 * that records when processing began.  Used to determine staleness.
 */
const PROCESSING_STATUS_FIELDS = {
  ciReportProcessingStatus:            'ciReportProcessingStartedAt',
  ssiReportProcessingStatus:           'ssiReportProcessingStartedAt',
  ddReportProcessingStatus:            'ddReportProcessingStartedAt',
  padReportProcessingStatus:           'padReportProcessingStartedAt',
  qbTestReportProcessingStatus:        'qbTestReportProcessingStartedAt',
  qbCheckReportProcessingStatus:       'qbCheckReportProcessingStartedAt',
  preARReportProcessingStatus:         'preARReportProcessingStartedAt',
  ratingScalesReportProcessingStatus:  'ratingScalesReportProcessingStartedAt',
  cpReportProcessingStatus:            'cpReportProcessingStartedAt',
  prReportProcessingStatus:            'prReportProcessingStartedAt',
  sembleMDTReportProcessingStatus:     'sembleMDTReportProcessingStartedAt',
};

/**
 * Examine all processing status fields and return a Firestore update
 * object that resets any statuses stuck at 'processing' for longer than
 * `maxAgeMs` back to 'idle'.
 *
 * @param {Object}  manageReports  The `dataModel.6_manageReports` object
 *                                 from Firestore.
 * @param {number}  maxAgeMs       How long a 'processing' status is
 *                                 allowed before it is considered stuck.
 *                                 Defaults to 10 minutes.
 * @returns {Object}  A (possibly empty) map of Firestore dot-path fields
 *                    to their reset value ('idle').  Pass directly to
 *                    `updateDoc`.
 */
// Default threshold raised from 10 to 15 minutes.  The server-side
// generate+render CF has a timeout of 540s and the backend reconciliation
// sweep runs at ~12 min, so the UI reset must be last in line:
//   CF timeout (9 min) < sweep (12 min) < client reset (15 min)
// This ensures the sweep gets a chance to actually complete a stuck report
// before the UI resets the spinner to 'idle'.
export const getStaleProcessingResets = (manageReports, maxAgeMs = 15 * 60 * 1000) => {
  const now = Date.now();
  const resets = {};

  for (const [statusField, startedAtField] of Object.entries(PROCESSING_STATUS_FIELDS)) {
    if (manageReports?.[statusField] !== 'processing') continue;

    const startedAt = manageReports?.[startedAtField];

    // If no timestamp exists (legacy patient or timestamp write failed),
    // treat as stuck — the absence of a timestamp means we can't prove
    // it's recent, and the safe default is to unblock the UI.
    const rawMs = startedAt?.toMillis?.() ?? (startedAt?.seconds != null ? startedAt.seconds * 1000 : 0);

    if (now - rawMs > maxAgeMs) {
      resets[`dataModel.6_manageReports.${statusField}`] = 'idle';
    }
  }

  return resets;
};

/**
 * Wait for a specific report's processing status to leave 'processing'.
 *
 * Sets up a lightweight Firestore onSnapshot listener on the patient document
 * and resolves the returned promise as soon as the target status field is no
 * longer 'processing'.  If the status is already non-processing on the first
 * snapshot, resolves immediately.
 *
 * A timeout guard ensures we never hang forever — if the status doesn't
 * change within `timeoutMs`, the listener is cleaned up and the promise
 * resolves with `null` (the caller can treat this as "gave up waiting").
 *
 * The `getStatus` selector lets callers point at status fields that live
 * OUTSIDE the ADHD `dataModel.6_manageReports` sub-document without forking a
 * second waiter.  The Adult Autism patient report, for example, stores its
 * status on the patient header at `autismPatientReport.prReportProcessingStatus`,
 * so it passes a selector reading that path.  The default preserves the
 * original ADHD behaviour so existing callers are unaffected.
 *
 * @param {string}   patientId   Firestore patient document ID.
 * @param {string}   statusField The processing status field name inside
 *                               `dataModel.6_manageReports`, e.g.
 *                               'prReportProcessingStatus'.  Used only by the
 *                               default selector; ignored when a custom
 *                               `getStatus` is supplied.
 * @param {number}   timeoutMs   Max time to wait (default 5 minutes).
 * @param {Function} getStatus   Optional selector `(patientData) => status`.
 *                               Defaults to the ADHD 6_manageReports accessor.
 * @returns {Promise<string|null>}  The final status value, or null on timeout.
 */
export const waitForProcessingComplete = (
  patientId,
  statusField,
  timeoutMs = 5 * 60 * 1000,
  getStatus = (data) => data?.dataModel?.['6_manageReports']?.[statusField],
) => {
  return new Promise((resolve) => {
    const patientRef = doc(db, 'patients', patientId);

    const timeout = setTimeout(() => {
      unsub();
      resolve(null);
    }, timeoutMs);

    const unsub = onSnapshot(
      patientRef,
      (snap) => {
        const status = getStatus(snap.data());
        if (status !== 'processing') {
          clearTimeout(timeout);
          unsub();
          resolve(status ?? null);
        }
      },
      (error) => {
        console.error('waitForProcessingComplete snapshot error:', error);
        clearTimeout(timeout);
        unsub();
        resolve(null);
      }
    );
  });
};

/**
 * Compute a self-heal reset for a stuck Adult Autism Patient Report.
 *
 * Unlike the ADHD pipeline (whose statuses live under `6_manageReports` and are
 * swept by both a backend reconcile CF and the dashboard's getStaleProcessingResets),
 * the Autism patient report status lives on the patient header under
 * `autismPatientReport.prReportProcessingStatus`.  There is now a backend sweep
 * (reconcile_stuck_reports.py) that replays a stuck render after ~12 minutes, but
 * this client helper is the fast, in-tab fallback: if the dashboard is open when
 * a render has clearly died, it flips the status to 'error' so the clinician's
 * "Try Again" retry path becomes reachable.
 *
 * We reset to 'error' (not 'idle') deliberately: the Autism dashboard has no
 * manual "Generate" button, so 'idle' would strand the row with no way forward,
 * whereas 'error' surfaces the retry button.
 *
 * The threshold (15 min) sits AFTER the backend sweep (12 min) so the sweep gets
 * first chance to actually complete the report before the UI gives up on it —
 * mirroring ADHD's CF-timeout < backend-sweep < client-reset ordering.
 *
 * @param {Object} autismPatientReport The patient header's `autismPatientReport`
 *                                     map (or null/undefined before load).
 * @param {number} maxAgeMs           How long 'processing' may persist before it
 *                                     is considered stuck.  Defaults to 15 min.
 * @returns {Object} A (possibly empty) map of Firestore dot-path fields to their
 *                   reset value, ready to pass to `updateDoc`.
 */
export const getStaleAutismReportReset = (autismPatientReport, maxAgeMs = 15 * 60 * 1000) => {
  if (autismPatientReport?.prReportProcessingStatus !== 'processing') return {};

  const startedAt = autismPatientReport?.prReportProcessingStartedAt;

  // If no start timestamp exists (write failed, or a legacy record), we can't
  // prove the render is recent — treat as stuck so the UI is unblocked.
  const startedMs = startedAt?.toMillis?.() ?? (startedAt?.seconds != null ? startedAt.seconds * 1000 : 0);

  if (Date.now() - startedMs > maxAgeMs) {
    return { 'autismPatientReport.prReportProcessingStatus': 'error' };
  }

  return {};
};

/**
 * Compute a self-heal reset for a stuck CYP ADHD Patient Report.
 *
 * CYP Patient Report processing status lives on the reportContent doc at
 * `patientReportReportProcessingStatus` (prefix 'patientReport'). The same
 * threshold ordering applies: CF timeout < backend sweep < client reset.
 *
 * Clinician-only: Firestore rules forbid Support writes to CYP reportContent
 * docs, so this reset is only usable by clinicians. The dashboard must gate
 * on the user's role before applying.
 *
 * @param {Object} patientReportDoc  The CYP reportContent/patientReport doc
 *                                   data (or null/undefined before load).
 * @param {number} maxAgeMs          How long 'processing' may persist before
 *                                   it is considered stuck. Defaults to 15 min.
 * @returns {Object} A (possibly empty) map of Firestore field names to their
 *                   reset value, ready to pass to updateDoc on the PR doc.
 */
export const getStaleCypPatientReportReset = (patientReportDoc, maxAgeMs = 15 * 60 * 1000) => {
  if (patientReportDoc?.patientReportReportProcessingStatus !== 'processing') return {};

  const startedAt = patientReportDoc?.patientReportReportProcessingStartedAt;

  // If no start timestamp exists, treat as stuck so the UI is unblocked.
  const startedMs = startedAt?.toMillis?.() ?? (startedAt?.seconds != null ? startedAt.seconds * 1000 : 0);

  if (Date.now() - startedMs > maxAgeMs) {
    return { patientReportReportProcessingStatus: 'error' };
  }

  return {};
};

/**
 * Wait for a CYP modular stage's render to leave 'processing'.
 *
 * CYP stage processing statuses live on their reportContent docs (not the
 * patient header), so the shared `waitForProcessingComplete` (which listens
 * to the patient header doc) cannot be reused directly. This waiter listens
 * to the reportContent doc for the given stage and resolves when the status
 * field transitions away from 'processing'.
 *
 * @param {string} patientId  Firestore patient document ID.
 * @param {string} stageKey   reportContent doc key (e.g. 'patientReport',
 *                            'clinicalInterview').
 * @param {number} timeoutMs  Max time to wait (default 12 min).
 * @returns {Promise<string|null>} The final status value, or null on timeout/error.
 */
export const waitForStageRenderComplete = (patientId, stageKey, timeoutMs = 12 * 60 * 1000) => {
  // Lazy import: avoid circular deps at module load. The path helper is a
  // pure function with no side effects.
  const { getCypAdhdReportContentPathSegments } = require('../constants/cypAdhdFirestorePaths');
  const { STAGE_TO_REPORT_PREFIX } = require('../constants/cypAdhdStages');

  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  const statusField = `${prefix}ReportProcessingStatus`;
  const ref = doc(db, ...getCypAdhdReportContentPathSegments(patientId, stageKey));

  return new Promise((resolve) => {
    // `unsub` is assigned by onSnapshot's return value, but a listener can be
    // invoked synchronously (cached data) before that assignment lands, so
    // every exit path goes through `settle`, which unsubscribes once the
    // handle exists and otherwise defers the unsubscribe to just after.
    let unsub = null;
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (unsub) unsub();
      resolve(value);
    };

    const timeout = setTimeout(() => settle(null), timeoutMs);

    unsub = onSnapshot(
      ref,
      (snap) => {
        const status = snap.data()?.[statusField];
        if (status !== 'processing') settle(status ?? null);
      },
      (error) => {
        console.error('waitForStageRenderComplete snapshot error:', error);
        settle(null);
      },
    );
    // Listener fired synchronously before `unsub` existed: detach now.
    if (settled && unsub) unsub();
  });
};
